import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { SERVICES } from "@/data/site";
import { SERVICE_CONFIGS } from "@/data/serviceConfigs";
import ServicePage from "@/components/service/ServicePage";
import Seo, { breadcrumbLd, serviceLd } from "@/components/seo/Seo";

// Extra market-specific keywords per service — targets the six markets:
// UK, USA, Germany, KSA, UAE, Pakistan.
const SVC_KEYWORDS = {
  "software-web-development":
    "custom software development, software development company UK, software house Islamabad, bespoke SaaS, enterprise software Pakistan, custom web development London, Softwareentwicklung Deutschland, software development Dubai, Riyadh",
  "mobile-app-development":
    "mobile app development company, iOS app development UK, Android app development Pakistan, React Native developers, Flutter developers, mobile app agency Dubai, App-Entwicklung Deutschland, mobile app KSA",
  "digital-marketing-seo":
    "SEO agency UK, digital marketing Pakistan, SEO services USA, SEO Dubai, SEO Riyadh, social media marketing agency, SEO Deutschland",
  "staff-augmentation":
    "staff augmentation, IT staff augmentation UK, offshore engineers Pakistan, remote developers Dubai, tech talent Riyadh, contract engineers Germany",
  "cloud-devops":
    "cloud infrastructure services, AWS partner UK, Azure services Pakistan, DevOps consulting USA, Kubernetes experts, cloud migration Dubai, cloud services Riyadh, Cloud DevOps Deutschland",
  "ai-data-science":
    "AI development company, AI consulting UK, AI Pakistan, ML engineers, LLM copilots, GPT integration, AI voice agents, Data science services, KI Softwareentwicklung Deutschland",
  "crm-development":
    "CRM development company UK, custom CRM development, bespoke CRM software, admissions CRM UK, sales CRM Pakistan, HubSpot alternative, Salesforce alternative, CRM software house UK",
  "web-app-development":
    "web application development UK, web app development company, SaaS development UK, custom web app Pakistan, React web app agency, Next.js developers UK, dashboard development",
  "ecommerce-development":
    "ecommerce development company UK, Shopify Plus agency UK, headless commerce, custom ecommerce development Pakistan, WooCommerce experts, Medusa developers, D2C store development UK",
  "saas-development":
    "SaaS development company UK, SaaS MVP development, multi-tenant SaaS Pakistan, Stripe billing, SaaS engineering agency UK, B2B SaaS developers, product engineering",
  "erp-development":
    "ERP development company UK, custom ERP software, bespoke ERP Pakistan, campus ERP UK, manufacturing ERP, HRMS software, payroll software, education ERP UK",
  "ai-development":
    "AI development company, AI development services, AI software development, AI business automation, LLM integration, AI copilots, AI agents, AI voice agents, document AI, RAG development",
  "ai-agent-development":
    "AI agent development, AI agent development company, autonomous AI agents, multi-tool AI agents, LangGraph developers, AI voice agent, AI sales agent, AI support agent, AI receptionist",
  "custom-software-development":
    "custom software development, custom software development company, bespoke software development, enterprise software development, business software development, custom software developers, software development services",
  "web-application-development":
    "web application development, web application development company, custom web app development, SaaS web application, web dashboard development, portal development, React web app development, Next.js developers",
  "edtech-development":
    "EdTech software development, EdTech development company, education software development, admissions CRM development, LMS development company, student portal development, campus ERP development, AI for education",
  "mvp-development":
    "MVP development, MVP development company, startup MVP development, SaaS MVP development, AI MVP development, product MVP development, MVP development agency, MVP developers",
  "software-modernisation":
    "software modernisation, software modernization, legacy software modernisation, legacy application modernisation, legacy code refactoring, cloud migration services, legacy system rewrite, legacy PHP modernisation, legacy .NET modernisation",
};

// Legacy slug → canonical slug mapping. Old URLs stay live but the <Seo>
// canonical points at the new URL so Google consolidates over time.
export const SVC_CANONICAL_ALIAS = {
  "ai-data-science": "ai-development",
  "software-web-development": "custom-software-development",
  "web-app-development": "web-application-development",
};

// Phase 3.3 — per-service ProofStrip whitelist. Each service page renders
// ONLY the metrics genuinely relevant to that capability (never the same
// mechanical set across all services). Metrics still gate on
// `approvedForPublicDisplay === true` in projectMetrics.js.
const SVC_PROOF_METRIC_IDS = {
  "ai-development": [
    "unicrm-document-vetting-time",
    "unicrm-ai-agents-per-workspace",
  ],
  "ai-agent-development": [
    "unicrm-ai-agents-per-workspace",
    "unicrm-whatsapp-availability",
  ],
  "crm-development": [
    "unicrm-counsellor-load-ratio",
    "unicrm-pilot-leads-handled",
    "unicrm-whatsapp-availability",
  ],
  "saas-development": [
    "unicrm-pilot-leads-handled",
    "unicrm-ai-agents-per-workspace",
  ],
  "edtech-development": [
    "unicrm-document-vetting-time",
    "unicrm-counsellor-load-ratio",
  ],
};

export default function ServiceDetail() {
  const { slug } = useParams();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [slug]);

  const config = SERVICE_CONFIGS[slug];
  const s = SERVICES.find((x) => x.slug === slug);

  if (config) {
    // If the URL is a legacy slug (e.g. ai-data-science) we still render the
    // page but declare the modern URL as canonical so Google consolidates.
    const canonicalSlug = SVC_CANONICAL_ALIAS[slug] || slug;
    const canonicalUrl = `https://www.cloudlabweb.com/services/${canonicalSlug}`;
    return (
      <>
        <Seo
          title={config.name}
          description={config.hero.subtitle}
          keywords={SVC_KEYWORDS[slug]}
          image={config.hero_image}
          canonical={canonicalUrl}
          type="website"
          jsonLd={[
            serviceLd({ name: config.name, description: config.hero.subtitle, slug: canonicalSlug }),
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: "Services", path: "/services" },
              { name: config.name, path: `/services/${canonicalSlug}` },
            ]),
          ]}
        />
        <ServicePage
          config={config}
          proofMetricIds={SVC_PROOF_METRIC_IDS[canonicalSlug] || null}
        />
      </>
    );
  }

  if (!s) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-40 text-center">
        <Seo title="Service not found" noindex />
        <h1 className="font-heading font-bold text-3xl">Service not found</h1>
        <Link to="/services" className="mt-4 inline-block text-primary">Back to services</Link>
      </div>
    );
  }

  return (
    <div data-testid="service-detail-page" className="max-w-5xl mx-auto px-6 py-24">
      <Seo title={s.name} description={s.tagline} />
      <Link to="/services" className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-primary">← All services</Link>
      <h1 className="mt-6 font-heading font-black tracking-tight text-5xl">{s.name}</h1>
      <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{s.tagline}</p>
      <p className="mt-4 max-w-2xl text-foreground/80">{s.description}</p>
    </div>
  );
}
